import { formatNumber } from '@angular/common';
import { Directive, ElementRef, HostListener, Inject, Input, LOCALE_ID, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[slMoneyCentControl]',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: SlMoneyCentValueAccessorDirective, multi: true }],
})
export class SlMoneyCentValueAccessorDirective implements ControlValueAccessor {
  
  private groupSep: string;
  private decSep: string;
  private latestValue: number = 0.0;
  private _taxRate = 25.0;

  @Input() trigger: 'value' | 'input' = 'value';

  constructor(private element: ElementRef<HTMLElement>, private renderer: Renderer2, @Inject(LOCALE_ID) private locale: string ) {

    const tmp = formatNumber(1234.50, locale).replace(/[0-9]/g, '');
    this.groupSep = tmp[0];
    this.decSep = tmp[1];
    this.onChange = () => { };

    this.element.nativeElement.addEventListener('slBlur', () => {
      this.writeValue(this.latestValue);
    });
  }

  onChange?: (value: number) => void;

  writeValue(value: number) {
    if(this.element.nativeElement === document.activeElement) {
      return;
    }
    this.latestValue = value;
    if(value > 0) {
      value = this.addTax(value);
    }
    const formattedValue = formatNumber(value / 100.00, this.locale, '1.2-2');
    this.renderer.setProperty(this.element.nativeElement, 'value', formattedValue);
  }

  @HostListener('slInput', ['$event'])
  handleInput(event: CustomEvent) {
    if(this.trigger === 'input') {
      let value = event.detail as string;
      this.handleValue(value);
      event.stopPropagation();
    }
  }

  @HostListener('valueChange', ['$event'])
  handleValueChange(event: CustomEvent) {
    if(this.trigger === 'value') {
      let value = event.detail as string;
      this.handleValue(value);
      event.stopPropagation();
    }
  }

  registerOnChange(fn: (value: number) => void) {
    this.onChange = value => {
      fn(value);
    };
  }

  registerOnTouched() { }

  get taxRate() {
    return this._taxRate;
  }

  @Input() 
  set taxRate(value: number) {
    this._taxRate = value;
    this.writeValue(this.latestValue);
  }

  private handleValue(value: string) {
    if(value.indexOf(this.decSep) < 0) {
      value += this.decSep + '00';
    }

    if(value) {
      value = value.replace(new RegExp('\\' + this.groupSep, "g"), '');
      value = value.replace(new RegExp('\\' + this.decSep, "g"), '.');
    } else {
      value = '0.00';
    }

    let valueNumber = parseFloat(value);
    const revertingTaxPercentage = valueNumber / this.addTax(valueNumber);
    valueNumber *= revertingTaxPercentage;
  
    const multiplied = valueNumber * 100
    const result = Math.round(multiplied);
    this.latestValue = result;
    this.onChange!(result);

    // Write value to ensure that field is correctly formatted
    this.writeValue(result);
  }

  private addTax(value: number) {
    return value + (value * (this.taxRate / 100));
  }
}